import React from "react";
import {
  Box,
  Hidden,
  Heading,
  Text,
  Columns,
  Column,
  TileLink,
} from "@origin-digital/ods-core";
import {
  IconSupportAgent,
  IconChevronRightSmall,
} from "@origin-digital/ods-icons";
import { useFeatureFlag } from "@origin-digital/feature-flags";
import { isNative } from "@origin-digital/platform-helpers";
import { navTo, authUser } from "@origin-digital/event-dispatcher";
import {
  interaction,
  type IInteractionData,
} from "@origin-digital/reporting-client";
import { config } from "../../config";
import { content } from "src/components/ContactUs/content";

const trackOpenChatWidget = (native: boolean) => {
  interaction("click", {
    componentType: "button",
    id: "open-chat",
    parentContainer: "intercom",
    label: "open chat widget",
    isNative: native,
    eVar10: "Yes",
  } as IInteractionData);
};

const ContactUsTitleRow = () => (
  <Box
    display="flex"
    width="full"
    flexDirection="row"
    alignItems="center"
    justifyContent={{ xs: "space-between", lg: "flex-start" }}
    gap="small"
  >
    <Heading component="h3" variant="h2">
      {content.headline}
    </Heading>
    <Box display="flex" position="relative" left="xxsmall">
      <IconChevronRightSmall
        variant="solid"
        size="xsmall"
        color="neutralDark"
      />
    </Box>
  </Box>
);

const ContactUsTileHeader = () => (
  <Box
    display="flex"
    width="full"
    flexDirection={{ xs: "column", lg: "row-reverse" }}
    paddingX={{ xs: "large", sm: "xlarge" }}
    height="full"
    gap={{ xs: "medium", lg: "xlarge", xl: "xxlarge" }}
  >
    <Hidden above="lg">
      <Box display="flex">
        <IconSupportAgent size="smallmedium" />
      </Box>
    </Hidden>
    <Hidden below="lg">
      <Box display="flex">
        <IconSupportAgent size="medium" />
      </Box>
    </Hidden>
    <Box display="flex" width="full" flexDirection="column" gap="small">
      <ContactUsTitleRow />
      <Text tone="neutralDark">{content.byline}</Text>
    </Box>
  </Box>
);

const ContactUsTileBody = () => (
  <Box
    display="flex"
    width="full"
    flexDirection="column"
    paddingY={{ xs: "xlarge", lg: "xxlarge" }}
  >
    <ContactUsTileHeader />
  </Box>
);

const ContactUsTile: React.FC<{
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
}> = ({ onClick }) => (
  // `href` is the fallback target for right-click "Open in new tab" and
  // crawlers/screen readers; left-click navigation is handled by the
  // wrapping Box.onClick which calls preventDefault + dispatches navTo.
  <Box display="flex" height="full" width="full" onClick={onClick}>
    <TileLink
      href={content.href}
      dataId="contact-us"
      hideChevron
      cardProps={{
        borderRadius: "medium",
        backgroundColor: "cardBackgroundSecondary",
        paddingXSpecific: "none",
        paddingYSpecific: "none",
        height: "full",
      }}
    >
      <ContactUsTileBody />
    </TileLink>
  </Box>
);

export const ContactUs: React.FC = () => {
  const chatForContactUs = useFeatureFlag("chatForContactUs");
  const native = isNative();

  const handleTileClick = async (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (chatForContactUs) {
      const isAuth =
        native ||
        (
          await authUser({
            auth0Environment: config.env as EnvironmentNames,
          })
        )?.isAuth;
      if (isAuth) {
        trackOpenChatWidget(native);
        navTo.messageUs(undefined);
        return;
      }
    }
    navTo.contactUs(undefined);
  };

  return (
    <Box id="contact" marginX={{ xs: "medium", sm: "none" }}>
      <Box marginBottom={{ xs: "medium", lg: "large" }}>
        <Heading component="h2" variant="h4">
          {content.heading}
        </Heading>
      </Box>
      <Columns collapseBelow="lg" space={{ xs: "medium", xl: "large" }}>
        <Column width="1/2" alignSelf="stretch">
          <ContactUsTile onClick={handleTileClick} />
        </Column>
      </Columns>
    </Box>
  );
};
